import { authResponse } from "Utils/authentication";
import { routerUrl } from "Utils/routerUrl";

import { appendAuthParams, AUTH_TOKEN_NAMES, getAuthToken, setAuthToken, createAuthToken, isValidAuthToken } from "Common/TokenAuth/Utils";

const fetchBaseForRequestObject = (request, config) =>
  authResponse.promise.then(({ csrfToken }) => fetch(new Request(request, fetchConfig({ csrfToken, config })))).then(responseHandler);

const fetchBase = (url, config) =>
  authResponse.promise.then(({ csrfToken }) => fetch(appendAuthParams(url), fetchConfig({ csrfToken, config }))).then(responseHandler);

const fetchConfig = ({ csrfToken, config: { headers, ...rest } }) => ({
  headers: {
    ...authTokenHeaders(),
    "X-Requested-With": "XMLHttpRequest",
    "X-CSRF-Token": csrfToken.value,
    ...headers,
  },

  credentials: "same-origin",
  ...rest,
});

const authTokenHeaders = () => {
  const authToken = getAuthToken();
  return isValidAuthToken(authToken)
    ? {
        [AUTH_TOKEN_NAMES.UID]: authToken.uid,
        [AUTH_TOKEN_NAMES.EXPIRY]: authToken.expiry,
        [AUTH_TOKEN_NAMES.CLIENT]: authToken.client,
        [AUTH_TOKEN_NAMES.TOKEN_TYPE]: authToken.tokenType,
        [AUTH_TOKEN_NAMES.ACCESS_TOKEN]: authToken.accessToken,
      }
    : {};
};

/**
 * Function used by RTK Query to prepare headers with CSRF token
 * https://github.com/reduxjs/redux-toolkit/blob/v1.9.7/packages/toolkit/src/query/fetchBaseQuery.ts#L235
 * @param headers Headers object: https://developer.mozilla.org/en-US/docs/Web/API/Headers
 * @param param1 Functionality provided by RTK Query. Not used in this function.
 * @returns A promise that resolves to the Headers object with additional authentication-related headers
 */
export const prepareHeadersWithCSRFToken = async (headers, { getState: _getState, extra: _extra, endpoint: _endpoint, forced: _forced, type: _type }) => {
  const { csrfToken } = await authResponse.promise;
  const authHeaders = {
    "X-Requested-With": "XMLHttpRequest",
    "X-CSRF-Token": csrfToken.value,
    ...authTokenHeaders(),
  };
  Object.keys(authHeaders).forEach((key) => {
    headers.set(key, authHeaders[key]);
  });
  return headers;
};

const responseHandler = (response) => {
  // merge in token auth headers
  const token = createAuthToken(
    response.headers.get(AUTH_TOKEN_NAMES.ACCESS_TOKEN),
    response.headers.get(AUTH_TOKEN_NAMES.CLIENT),
    response.headers.get(AUTH_TOKEN_NAMES.EXPIRY),
    response.headers.get(AUTH_TOKEN_NAMES.UID),
    response.headers.get(AUTH_TOKEN_NAMES.TOKEN_TYPE),
  );

  setAuthToken(token);

  // If we have gotten logged out, pass the failure on, but invalidate our
  // authentication information.
  if (response.status === 401) {
    authResponse.invalidate();
    // If the response is a JSON blob including a "location" value, use that
    // as the redirect. Otherwise, use the default one.
    const contentType = response.headers.get("Content-Type");
    // TODO: This code can't work because it has to resolve the promise to make the response body available in the next closure
    const redirectUrlPromise =
      contentType && contentType.indexOf("application/json") !== -1 ? response.json().then((json) => json.location) : Promise.resolve(null);
    redirectUrlPromise.then((url) => {
      window.location.href = url || "/login?timedout=true";
    });
    // Return a promise that never resolves (since we are redirecting away)
    return new Promise(() => null);
  }
  if (response.status === 503) {
    window.location.href = `${routerUrl}/error_503`;
    // Return a promise that never resolves (since we are redirecting away)
    return new Promise(() => null);
  }
  return response;
};

// When the Content-Type is not defined, fetch will let the client/browser
// decide how to set the content type. This is particularly useful when
// the request is uploading form content. This way the boundary parameter
// in the content type will be set for you. Otherwise the whole request
// data would have to be built from scratch, including reading the files
// before adding them to the request.
export const fetchWithCSRFTokenNoContentType = fetchBase;

// When the input is a Request object.
// https://developer.mozilla.org/en-US/docs/Web/API/Request/Request
export const fetchWithCSRFTokenForRequestObject = (request) =>
  fetchBaseForRequestObject(request, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

export const fetchWithCSRFTokenForFormData = (request) =>
  fetchBaseForRequestObject(request, {
    headers: {
      "Content-Type": "multipart/form-data",
      Accept: "application/json",
    },
  });

interface FetchWithCSRFTokenOptions {
  headers?: Record<string, string>;
  method?: string;
  body?: string;
}

const fetchWithCSRFToken = (url: string, { headers, ...rest }: FetchWithCSRFTokenOptions) =>
  fetchBase(url, {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...headers,
    },

    ...rest,
  });

export default fetchWithCSRFToken;

export interface XHREventContext<T = any> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: any) => void;
  xhr: XMLHttpRequest;
}

export interface XHRWithCSRFTokenOptions<T = any> {
  url: string;
  method?: string;
  data?: any;
  headers?: Record<string, string>;
  events?: {
    [K in keyof XMLHttpRequestEventMap]?: (e: XMLHttpRequestEventMap[K], ctx: XHREventContext<T>) => void;
  };
}

export async function XHRWithCSRFToken<T = any>(options: XHRWithCSRFTokenOptions<T>): Promise<T> {
  const { csrfToken } = await authResponse.promise;
  return new Promise<T>((resolve, reject) => {
    const { url, method = "GET", data: inputData = null, headers = {}, events = {} } = options;

    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);

    // CSRF token
    xhr.withCredentials = true;
    if (csrfToken) xhr.setRequestHeader("X-CSRF-Token", csrfToken.value);

    // Default headers
    xhr.setRequestHeader("Accept", "application/json");
    // xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest"); // Already set automatically

    let data = inputData;
    if (data && !(data instanceof FormData)) {
      xhr.setRequestHeader("Content-Type", "application/json");
      data = JSON.stringify(data);
    }

    // Custom headers
    for (const [key, value] of Object.entries(authTokenHeaders())) {
      xhr.setRequestHeader(key, value);
    }
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    // Hook up custom events
    for (const [event, handler] of Object.entries(events) as [keyof XMLHttpRequestEventMap, (e: Event, ctx: XHREventContext<T>) => void][]) {
      xhr.addEventListener(event, (e) => handler(e, { resolve, reject, xhr }));
    }

    xhr.onload = () => {
      // Handle 401/503 like fetch
      if (xhr.status === 401) {
        authResponse.invalidate();
        try {
          const json = JSON.parse(xhr.responseText);
          window.location.href = json.location || "/login?timedout=true";
        } catch {
          window.location.href = "/login?timedout=true";
        }
        return;
      }
      if (xhr.status === 503) {
        window.location.href = `${routerUrl}/error_503`;
        return;
      }

      // Check for HTTP error status codes
      if (xhr.status >= 400) {
        let errorMessage = "Request failed";
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          // Extract error message from common backend response formats
          errorMessage = errorResponse.error || errorResponse.message || errorResponse.errors || errorMessage;
        } catch {
          // If response is not JSON, use status text or response text
          errorMessage = xhr.responseText || xhr.statusText || errorMessage;
        }
        reject(new Error(errorMessage));
        return;
      }

      // Try to parse JSON, fallback to text
      try {
        resolve(JSON.parse(xhr.responseText));
      } catch {
        resolve(xhr.responseText);
      }
    };

    xhr.onerror = () => reject({ status: xhr.status || 0, response: xhr.statusText, xhr });

    xhr.send(data);
  });
}
